import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  LuSettings2, LuCpu, LuZap, LuTarget, LuShield, LuLink2,
  LuClock, LuLightbulb, LuFlag,
  LuFilm, LuMail, LuArrowRight,
  LuNewspaper, LuFileText, LuDownload,
  LuTrendingUp, LuAward, LuGlobe, LuPlane, LuGitMerge,
  LuLuggage, LuShieldCheck, LuScanLine, LuChevronLeft, LuChevronRight,
} from 'react-icons/lu';

/* ── Unsplash image helper ── */
const IMG = {
  heroMain:  'https://ak2.picdn.net/shutterstock/videos/25662092/thumb/10.jpg',
  checkin:   'https://i.pinimg.com/736x/31/1e/bb/311ebb3b9d54bf71080144b2f5ebcd2d.jpg',
  xray:      'https://as1.ftcdn.net/jpg/01/58/62/74/1000_F_158627455_Fhb86l3Y1XOQNJaKUr9TrtodJ9mbLZZR.jpg',
  sorting:   'https://s.yimg.com/ny/api/res/1.2/UpIj9lorEpX.1pvdgJDdWQ--/YXBwaWQ9aGlnaGxhbmRlcjt3PTEyMDA7aD02NzU7Y2Y9d2VicA--/https://media.zenfs.com/en/popular_science_109/efe95397d57776edf34595486739ef46',
  loading:   'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&q=80&auto=format',
  whatIs:    'https://media.licdn.com/dms/image/v2/D5622AQHcwvOqT-uLMg/feedshare-shrink_800/feedshare-shrink_800/0/1704976877350?e=2147483647&v=beta&t=fgxOoBcHTp3EoqPh2njEobTcFNAqxqRK2GA_g2xs01Q',
  rfid:      'https://images.travelandleisureasia.com/wp-content/uploads/sites/3/2024/07/23003746/Dubai-International-Airport-Dmitriy-Feldman-Svarshik-Shutterstock.jpg',
  airport1:  'https://mediacentre.heathrow.com/contents/archives/175/81/images/thumb1280x1683_width/heathrow_175811947172860_thumb.jpg',
  carousel:  'https://i.pinimg.com/1200x/3f/dc/ed/3fdced236521a5a2a2e0991cf0a4d5da.jpg',
  sorting2:  'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=900&q=80&auto=format',
  tech:      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&q=80&auto=format',
};

/* ══════════════════════════════════════════════════
   HERO SLIDER DATA
══════════════════════════════════════════════════ */
const HERO_SLIDES = [
  {
    id: 0,
    step: '01',
    tag: 'Check-In',
    title: 'Bag Tagged & Entered',
    headline: 'Every Journey\nStarts Here',
    desc: 'The moment a bag hits the belt at check-in, it\'s weighed, barcoded, and injected into a vast underground network that spans kilometres beneath your feet.',
    img: 'https://i.pinimg.com/1200x/2a/ec/66/2aec66e982c54b71d61d2c86c2675900.jpg',
    alt: 'Passenger placing luggage on check-in conveyor belt',
    accent: '#7c3aed',
    accentDark: '#6d28d9',
    accentPale: '#ede9fe',
    grad: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
    metrics: [{ v: '0.4s', l: 'Tag scan' }, { v: '100%', l: 'Read rate' }, { v: '24/7', l: 'Live' }],
    icon: <LuLuggage size={20} />,
    features: ['Barcode & weight capture', 'Passenger-bag match verified', 'Journey database entry'],
  },
  {
    id: 1,
    step: '02',
    tag: 'RFID Scan',
    title: 'Unique ID Captured',
    headline: 'Tracked in\nMilliseconds',
    desc: 'Multi-angle scanner arrays read every bag\'s RFID chip or barcode at full belt speed — instantly cross-referencing flight manifests and security clearances.',
    img: 'https://i.pinimg.com/736x/03/01/30/03013086a0b05a36725890974eb6c143.jpg',
    alt: 'RFID chip scanner array reading baggage tags',
    accent: '#0891b2',
    accentDark: '#0e7490',
    accentPale: '#cffafe',
    grad: 'linear-gradient(135deg,#0891b2,#0e7490)',
    icon: <LuScanLine size={20} />,
    features: ['RFID & 2D barcode reading', 'Flight manifest cross-check', 'Real-time database sync'],
  },
  {
    id: 2,
    step: '03',
    tag: 'Security',
    title: 'X-Ray & AI Screening',
    headline: 'Zero Threats\nGet Through',
    desc: 'High-resolution dual-energy X-ray tunnels scan every bag in under two seconds. AI models trained on millions of threat signatures flag anomalies instantly.',
    img: 'https://i.pinimg.com/1200x/c7/cd/10/c7cd10cceb9b1f8cae2e1bffad272127.jpg',
    alt: 'Airport baggage X-ray security screening tunnel',
    accent: '#db2777',
    accentDark: '#be185d',
    accentPale: '#fce7f3',
    grad: 'linear-gradient(135deg,#db2777,#be185d)',
    icon: <LuShieldCheck size={20} />,
    features: ['Dual-energy X-ray imaging', 'AI-powered threat detection', 'Automatic alarm routing'],
  },
  {
    id: 3,
    step: '04',
    tag: 'AI Sorting',
    title: 'Routed to the Right Gate',
    headline: 'Precision at\nMassive Scale',
    desc: 'Tilt-tray and cross-belt sorters read destination data and divert each bag to the exact gate chute — all at walking pace, all without human intervention.',
    img: 'https://images.stockcake.com/public/b/0/2/b02ff10d-b895-470d-b555-1e9e9a27c1c4_large/luggage-conveyor-journey-stockcake.jpg',
    alt: 'High-speed automated baggage sorting system',
    accent: '#059669',
    accentDark: '#047857',
    accentPale: '#d1fae5',
    grad: 'linear-gradient(135deg,#059669,#047857)',
    icon: <LuGitMerge size={20} />,
    features: ['Tilt-tray sorter technology', 'Dynamic re-routing on delays', 'Missed-connection recovery'],
  },
  {
    id: 4,
    step: '05',
    tag: 'Aircraft Load',
    title: 'Loaded Into the Hold',
    headline: 'Airside in\nUnder 20 min',
    desc: 'Bags arrive at the gate chute sequenced for optimal weight distribution. Ground crews load ULD containers and transfer them to the aircraft hold.',
    img: 'https://i.sstatic.net/qpC59.jpg',
    alt: 'Aircraft baggage hold loading from conveyor belt',
    accent: '#d97706',
    accentDark: '#b45309',
    accentPale: '#fff7ed',
    grad: 'linear-gradient(135deg,#d97706,#b45309)',
    icon: <LuPlane size={20} />,
    features: ['Weight-distribution sequencing', 'ULD container loading', 'Last-minute bag intercept'],
  },
];

/* ══════════════════════════════════════════════════
   HERO SLIDER  — premium card-over-image design
══════════════════════════════════════════════════ */
function HeroSlider() {
  const [active, setActive] = useState(0);
  const [prevIdx, setPrevIdx] = useState(null);
  const [dir, setDir]       = useState('next');
  const [busy, setBusy]     = useState(false);
  const timerRef = useRef(null);
  const total = HERO_SLIDES.length;

  const goTo = useCallback((idx, d = 'next') => {
    if (busy || idx === active) return;
    setBusy(true); setDir(d); setPrevIdx(active); setActive(idx);
    setTimeout(() => { setPrevIdx(null); setBusy(false); }, 550);
  }, [busy, active]);

  const goNext = useCallback(() => goTo((active + 1) % total, 'next'), [active, goTo, total]);
  const goPrev = useCallback(() => goTo((active - 1 + total) % total, 'prev'), [active, goTo, total]);

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(goNext, 5500);
  }, [goNext]);

  useEffect(() => {
    timerRef.current = setInterval(goNext, 5500);
    return () => clearInterval(timerRef.current);
  }, [goNext]);

  const s    = HERO_SLIDES[active];
  const prev = prevIdx !== null ? HERO_SLIDES[prevIdx] : null;

  return (
    <div className="hs-root" role="region" aria-label="Baggage journey slider">

      {/* ══ Left: content side ══ */}
      <div className="hs-left" data-step={s.step}>
        {/* Accent edge bar */}
        <div className="hs-accent-edge" style={{ background: s.grad }} />

        {/* Top eyebrow */}
        <div key={`ey-${active}`} className="hs-eyebrow hs-up">
          <span className="hs-eyebrow-dot" style={{ background: s.accent }} />
          <span className="hs-eyebrow-step">Step {s.step}</span>
          <span className="hs-eyebrow-sep" />
          <span className="hs-eyebrow-tag">{s.tag}</span>
        </div>

        {/* Headline */}
        <h1 key={`h-${active}`} className="hs-title hs-up hs-d1">
          {s.headline.split('\n').map((line, i) => (
            <span key={i}>
              {line}
              {i === 0 && <br />}
            </span>
          ))}
          <span className="hs-title-accent" style={{ background: s.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>.</span>
        </h1>

        {/* Description */}
        <p key={`d-${active}`} className="hs-desc hs-up hs-d2">
          {s.desc.split('—')[0].trim()}
        </p>

        {/* CTAs */}
        <div key={`cta-${active}`} className="hs-ctas hs-up hs-d4">
          <Link to="/how-it-works" className="hs-btn-solid" style={{ background: s.grad }}>
            <LuSettings2 size={15} /> How It Works
          </Link>
          <Link to="/technology" className="hs-btn-line">
            <LuCpu size={15} /> Technology
          </Link>
        </div>

        {/* Filmstrip nav */}
        <div className="hs-filmstrip" role="tablist" aria-label="Journey steps">
          {HERO_SLIDES.map((sl, i) => (
            <button
              key={i} role="tab"
              aria-selected={i === active}
              className={`hs-thumb ${i === active ? 'hs-thumb-active' : ''}`}
              onClick={() => { goTo(i, i > active ? 'next' : 'prev'); resetTimer(); }}
              aria-label={sl.tag}
            >
              <img src={sl.img} alt={sl.tag} loading="lazy" />
              {i === active && <div className="hs-thumb-bar" style={{ background: s.grad }} />}
            </button>
          ))}

          {/* Arrows */}
          <div className="hs-arr-group">
            <button className="hs-arr-btn" onClick={() => { goPrev(); resetTimer(); }} aria-label="Previous">
              <LuChevronLeft size={16} />
            </button>
            <button className="hs-arr-btn" onClick={() => { goNext(); resetTimer(); }} aria-label="Next">
              <LuChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="hs-progress" aria-hidden="true">
          <div className="hs-progress-fill" key={active} style={{ background: s.grad }} />
        </div>
      </div>

      {/* ══ Right: image panel ══ */}
      <div className="hs-right" aria-hidden="true">
        {prev && (
          <img key={`img-o-${prevIdx}`} src={prev.img} alt=""
            className="hs-rimg hs-rimg-out" />
        )}
        <img key={`img-i-${active}`} src={s.img} alt={s.alt}
          className="hs-rimg hs-rimg-in" />

        {/* Gradient scrim on left edge for blending */}
        <div className="hs-right-scrim" />

        {/* Live pill */}
        <div className="hs-live">
          <span className="hs-live-dot" />
          Live · Heathrow T5
        </div>

        {/* Floating info card on image */}
        <div className="hs-infocard" key={`ic-${active}`}>
          <div className="hs-infocard-icon" style={{ background: s.grad }}>
            {s.icon}
          </div>
          <div>
            <div className="hs-infocard-title">{s.title}</div>
            <div className="hs-infocard-sub">{s.tag} · Step {s.step}</div>
          </div>
        </div>

        {/* Step counter bottom-right */}
        <div className="hs-counter">
          <span className="hs-counter-cur" style={{ color: s.accent }}>{String(active + 1).padStart(2, '0')}</span>
          <span className="hs-counter-sep">/</span>
          <span className="hs-counter-tot">{String(total).padStart(2, '0')}</span>
        </div>
      </div>
    </div>
  );
}

/* ── YouTube Video Embed Section ── */
function VideoSection() {
  /* Using a real YouTube video about airport baggage systems */
  const chips = [
    { icon: <LuFilm size={13}/>,     label: 'Real Airport Footage' },
    { icon: <LuSettings2 size={13}/>,label: 'Automated System' },
    { icon: <LuGitMerge size={13}/>, label: 'Sorting in Action' },
    { icon: <LuPlane size={13}/>,    label: 'Gate Loading' },
  ];

  return (
    <section className="video-section">
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="text-center reveal" style={{ marginBottom: 44 }}>
          <div className="section-tag">Watch It In Action</div>
          <h2 className="section-title">
            See How Airport Conveyor Belts
            <span className="text-gradient">Really Work</span>
          </h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            A real-world airport baggage handling system in full operation — thousands of bags
            routed automatically every single hour, 24 hours a day.
          </p>
        </div>

        <div className="video-frame-wrap reveal">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://i.pinimg.com/1200x/8d/bd/fe/8dbdfe8a765da3ea5c860a62d34da7fd.jpg"
            className="local-video"
          >
            <source src="/airport-conveyor-video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <div className="video-chips reveal">
          {chips.map((c, i) => (
            <span key={i} className="video-chip">{c.icon} {c.label}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── What Is — Image + Text ── */
function WhatIsSection() {
  return (
    <section className="page-section bg-dark-2" aria-labelledby="whatIs">
      <div className="container">
        <div className="grid-2">
          <div className="reveal-left">
            <div className="section-tag">Overview</div>
            <h2 id="whatIs" className="section-title">What Is an Airport Conveyor Belt?</h2>
            <p style={{ color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.85 }}>
              An airport baggage conveyor belt system is a sophisticated automated transportation
              network that moves passenger luggage seamlessly from check-in to the aircraft — and
              back to the arrivals carousel on landing.
            </p>
            <p style={{ color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.85 }}>
              These systems span <strong>kilometres of underground track</strong> inside an airport,
              working silently beneath our feet. Modern installations integrate barcode scanners,
              RFID readers, AI-powered sorting machines, and real-time IoT monitoring to achieve
              near-perfect accuracy at massive scale — handling up to 10,000 bags per hour.
            </p>
            <div className="highlight-box" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <LuLightbulb size={20} style={{ flexShrink: 0, marginTop: 2, color: 'var(--primary)' }}/>
              <span>
                <strong>Did you know?</strong> London Heathrow's baggage system handles over
                180,000 bags every day through more than 60 km of conveyor tracks — all fully automated.
              </span>
            </div>
            <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/how-it-works" className="btn btn-primary">
                Learn How It Works <LuArrowRight size={16} />
              </Link>
              <Link to="/types" className="btn btn-outline">Explore Types</Link>
            </div>
          </div>
          <div className="reveal-right">
            <div className="what-is-img-card">
              <img src={IMG.whatIs} alt="Airport baggage conveyor belts underground" loading="lazy" />
              <div className="what-is-img-overlay">
                <div className="what-is-info-grid">
                  {[
                    { icon: <LuFlag size={18}/>,       title: 'Check-In',      desc: 'Bag tagged & entered into system' },
                    { icon: <LuShieldCheck size={18}/>, title: 'X-Ray Screen',  desc: 'Automated security scanning' },
                    { icon: <LuGitMerge size={18}/>,    title: 'AI Sorting',    desc: 'Routed to correct departure gate' },
                    { icon: <LuPlane size={18}/>,       title: 'Aircraft Hold', desc: 'Loaded by ground crew' },
                  ].map((item, i) => (
                    <div key={i} className="what-is-card">
                      <div className="wic-icon">{item.icon}</div>
                      <div className="wic-title">{item.title}</div>
                      <div className="wic-desc">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Gallery strip ── */
function GallerySection() {
  const images = [
    { src: IMG.checkin,  alt: 'Passenger check-in conveyor belt',      label: 'Check-in Belt' },
    { src: IMG.xray,     alt: 'Airport security X-ray tunnel',          label: 'Security Screening' },
    { src: IMG.sorting,  alt: 'Automated baggage sorting machine',      label: 'AI Sorting Hall' },
    { src: IMG.carousel, alt: 'Baggage claim carousel arrivals',        label: 'Claim Carousel' },
  ];
  return (
    <section className="page-section bg-dark-2" aria-labelledby="galleryTitle">
      <div className="container">
        <div className="text-center reveal" style={{ marginBottom: 44 }}>
          <div className="section-tag">Inside The System</div>
          <h2 id="galleryTitle" className="section-title">The System Up Close</h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            From check-in to aircraft — four zones that make the invisible journey possible.
          </p>
        </div>
        <div className="gallery-grid reveal">
          {images.map((img, i) => (
            <div key={i} className="gallery-card">
              <img src={img.src} alt={img.alt} loading="lazy" />
              <div className="gallery-label">{img.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── HOME PAGE ── */
function Home() {
  const features = [
    { icon: <LuZap size={24}/>,        title: 'Speed',        desc: 'Handles thousands of bags per hour, far exceeding any manual workforce capacity.' },
    { icon: <LuTarget size={24}/>,     title: 'Accuracy',     desc: 'Barcode & RFID tracking achieves 99.8%+ routing accuracy on every single journey.' },
    { icon: <LuShield size={24}/>,     title: 'Safety',       desc: 'Reduces workplace injuries and integrates fire, X-ray & automated threat detection.' },
    { icon: <LuLink2 size={24}/>,      title: 'Efficiency',   desc: 'Connects check-in, security, departure gates, and baggage claim in one seamless network.' },
  ];

  const stats = [
    { n: '4.5M+', l: 'Bags Daily',       d: "World's top 20 airports combined" },
    { n: '99.8%', l: 'Routing Accuracy', d: 'Achieved with full RFID systems' },
    { n: '60km+', l: 'Belt Network',     d: 'Single international hub airport' },
    { n: '40%',   l: 'Faster',           d: 'Compared to manual handling' },
    { n: '<1%',   l: 'Baggage Errors',   d: 'Modern automated sorting systems' },
  ];

  const airports = [
    { name: 'Heathrow, London',   bags: '180,000/day', belts: '60km+',   img: IMG.airport1 },
    { name: 'Dubai International',bags: '220,000/day', belts: '90km+',   img: IMG.rfid },
    { name: 'Singapore Changi',   bags: '150,000/day', belts: '50km+',   img: IMG.heroMain },
  ];

  return (
    <div className="page-enter">
      {/* ══ 1. HERO ── */}
      <section className="home-hero" aria-label="Hero">
        <HeroSlider />
      </section>

      {/* ══ 2. VIDEO ── */}
      <VideoSection />
      <div className="divider" />

      {/* ══ 3. WHAT IS ── */}
      <WhatIsSection />
      <div className="divider" />

      {/* ══ 4. GALLERY ── */}
      <GallerySection />
      <div className="divider" />

      {/* ══ 5. WHY IMPORTANT ── */}
      <section className="page-section" aria-labelledby="whyMatters">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Why It Matters</div>
            <h2 id="whyMatters" className="section-title">Why Conveyor Belts Are Critical</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              They are the backbone of every major airport, enabling millions of passengers
              to travel smoothly every single day without disruption.
            </p>
          </div>
          <div className="feature-grid-home">
            {features.map((f, i) => (
              <div key={i} className={`feature-card-home reveal delay-${i + 1}`}>
                <div className="fc-icon-wrap">{f.icon}</div>
                <div className="fc-title">{f.title}</div>
                <div className="fc-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ 6. STATS ── */}
      <section className="page-section bg-gradient-blue" aria-labelledby="statsTitle">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Real-World Impact</div>
            <h2 id="statsTitle" className="section-title">The Numbers Speak for Themselves</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              The scale and precision of modern airport baggage systems is extraordinary.
            </p>
          </div>
          <div className="grid-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            {stats.map((s, i) => (
              <div key={i} className={`stat-card reveal delay-${i + 1}`}>
                <div className="stat-number">{s.n}</div>
                <div className="stat-label">{s.l}</div>
                <div className="stat-desc">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ 7. WORLD AIRPORTS ── */}
      {/* <section className="page-section" aria-labelledby="airportsTitle">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">World Class</div>
            <h2 id="airportsTitle" className="section-title">The World's Biggest Baggage Systems</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              The largest airports on earth operate some of the most complex automated
              baggage systems ever built.
            </p>
          </div>
          <div className="airports-grid">
            {airports.map((a, i) => (
              <div key={i} className={`airport-card reveal delay-${i + 1}`}>
                <div className="airport-card-img">
                  <img src={a.img} alt={`${a.name} airport`} loading="lazy" />
                </div>
                <div className="airport-card-body">
                  <div className="airport-card-name">
                    <LuGlobe size={16} style={{ color: 'var(--primary)', flexShrink: 0 }}/>
                    {a.name}
                  </div>
                  <div className="airport-card-stats">
                    <div className="acs-item">
                      <LuLuggage size={13} />
                      <span>{a.bags}</span>
                    </div>
                    <div className="acs-item">
                      <LuTrendingUp size={13} />
                      <span>{a.belts} belt network</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* <div className="divider" /> */}

      {/* ══ 8. NEWSLETTER + BROCHURE CTA ── */}
      {/* <section className="page-section bg-gradient-blue" aria-labelledby="resourcesTitle">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 44 }}>
            <div className="section-tag">Free Resources</div>
            <h2 id="resourcesTitle" className="section-title">Stay Informed. Download. Learn More.</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Get our free monthly newsletter or download the 60-page technical PDF brochure — both completely free.
            </p>
          </div>
          <div className="home-resources-grid">
            <div className="home-resource-card reveal-left">
              <div className="home-resource-icon" style={{ background: 'var(--primary-pale)', color: 'var(--primary)' }}>
                <LuNewspaper size={28} />
              </div>
              <div className="home-resource-tag">Monthly Issue</div>
              <h3 className="home-resource-title">KI INNOVATION Newsletter</h3>
              <p className="home-resource-desc">
                Monthly deep-dives on airport baggage technology, RFID updates, safety bulletins,
                and industry news — curated for aviation professionals and enthusiasts.
              </p>
              <ul className="home-resource-list">
                {['Latest industry innovations', 'IATA stats & reports', 'Airport spotlights', '12,400+ subscribers'].map((item, i) => (
                  <li key={i}><span style={{ color: 'var(--primary)', marginRight: 7 }}>✓</span>{item}</li>
                ))}
              </ul>
              <Link to="/newsletter" className="btn btn-primary" style={{ marginTop: 4 }}>
                Subscribe Free <LuArrowRight size={15} />
              </Link>
            </div>
            <div className="home-resource-card reveal-right">
              <div className="home-resource-icon" style={{ background: 'var(--accent-pale)', color: 'var(--accent)' }}>
                <LuFileText size={28} />
              </div>
              <div className="home-resource-tag" style={{ color: 'var(--accent)', background: 'var(--accent-pale)', borderColor: 'rgba(8,145,178,.2)' }}>PDF Guide</div>
              <h3 className="home-resource-title">Free Technical Brochure</h3>
              <p className="home-resource-desc">
                A comprehensive 60-page PDF covering everything: system architecture, RFID tech,
                AI sorting, safety protocols, maintenance schedules, and future trends.
              </p>
              <ul className="home-resource-list">
                {['60 pages of technical depth', '40+ original diagrams', 'All six system chapters', 'Instant download, no account'].map((item, i) => (
                  <li key={i}><span style={{ color: 'var(--accent)', marginRight: 7 }}>✓</span>{item}</li>
                ))}
              </ul>
              <Link to="/brochure" className="btn btn-accent" style={{ marginTop: 4 }}>
                <LuDownload size={15} /> Download Free PDF
              </Link>
            </div>
          </div>
        </div>
      </section> */}

      {/* <div className="divider" /> */}

      {/* ══ 9. CTA BANNER ── */}
      <section className="page-section-sm">
        <div className="container">
          <div className="cta-banner reveal">
            <div className="cta-banner-bg-orb" />
            <LuAward size={40} style={{ color: 'var(--primary)', marginBottom: 16 }}/>
            <h2 className="section-title" style={{ marginBottom: 14 }}>Ready to Explore the Full System?</h2>
            <p className="section-sub" style={{ margin: '0 auto 32px' }}>
              Dive deeper into the technology, safety systems, and the different types of
              conveyor belts that keep airports running around the clock.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/how-it-works" className="btn btn-primary btn-lg">
                <LuSettings2 size={18} /> How It Works
              </Link>
              <Link to="/technology" className="btn btn-accent btn-lg">
                <LuCpu size={18} /> Technology
              </Link>
              <Link to="/contact" className="btn btn-outline btn-lg">
                <LuMail size={18} /> Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
